import React, { useState, useEffect, useRef } from "react";
import "./HotelBookingList.css"; // Reuse same CSS
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { getBookingsByAgentOwner } from "../services/hotelOwnerService";
import { useParams } from "react-router-dom";

import {
  getAgentEligibleAmount,
  getWithdrawalStatus,
  getWithdrawalHistory,
  saveBankDetails,
  requestAgentWithdrawal,
} from "../services/withdrawalService";

function AgentHotelBookingList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const { ownerId, agentDetailsId } = useParams();

  const [withdrawalSummary, setWithdrawalSummary] = useState({
    eligibleAmount: 0,
    lastAmount: 0,
    status: "Not Available",
    requestedAt: null,
    bankDetailsExist: false,
    history: [],
  });

  const [currentMonthWithdrawal, setCurrentMonthWithdrawal] = useState(0);

  const [showBankForm, setShowBankForm] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder_name: "",
    bank_name: "",
    bank_branch: "",
    account_number: "",
    expiry_month: "",
    expiry_year: "",
    cvc: "",
  });

  const sliderRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));

  const token = localStorage.getItem("jwt_token");

  useEffect(() => {
    if (!agentDetailsId) {
      console.error("Missing agentDetailsId from URL");
      return;
    }

    const fetchAgentBookings = async () => {
      try {
        const res = await getBookingsByAgentOwner(
          ownerId,
          agentDetailsId,
          token
        );
        console.log("Bookings API response:", res);

        if (res.success) setBookings(res.bookings);
      } catch (err) {
        console.error("Error loading agent bookings:", err);
      }
    };

    const loadWithdrawalData = async () => {
      try {
        const eligible = await getAgentEligibleAmount(token);
        const status = await getWithdrawalStatus(token);
        const historyData = await getWithdrawalHistory(token);

        setWithdrawalSummary({
          eligibleAmount: eligible.total || 0,
          lastAmount: status.amount || 0,
          status: status.status || "Not Available",
          requestedAt: status.requestedAt || null,
          bankDetailsExist: false,
          history: historyData || [],
        });
      } catch (err) {
        console.error("Error fetching withdrawal:", err);
      }
    };

    fetchAgentBookings();
    loadWithdrawalData();
  }, [ownerId, user?.id, token, agentDetailsId]);

  useEffect(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let total = 0;
    if (withdrawalSummary.history) {
      withdrawalSummary.history.forEach((item) => {
        const requestedAt = new Date(item.requestedAt);
        if (
          item.status === "Completed" &&
          requestedAt.getMonth() === currentMonth &&
          requestedAt.getFullYear() === currentYear
        ) {
          total += item.amount;
        }
      });
    }
    setCurrentMonthWithdrawal(total);
  }, [withdrawalSummary.history]);

  /** Format Date */
  const formatDate = (date) => new Date(date).toISOString().split("T")[0];
  const today = formatDate(new Date());

  /** Tabs Filtering */
  const getFilteredByTab = () => {
    if (activeTab === "today") {
      return bookings.filter((b) => formatDate(b.checking_date) === today);
    }
    if (activeTab === "upcoming") {
      return bookings.filter((b) => formatDate(b.checking_date) > today);
    }
    if (activeTab === "past") {
      return bookings.filter((b) => formatDate(b.checkout_date) < today);
    }
    return bookings;
  };

  const filteredBookings = getFilteredByTab().filter((b) =>
    b.listing?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /** Slider Scrolling */
  const scroll = (direction) => {
    if (sliderRef.current) {
      const { scrollLeft, clientWidth } = sliderRef.current;
      const scrollAmount = direction === "left" ? -clientWidth : clientWidth;
      sliderRef.current.scrollTo({
        left: scrollLeft + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  /** Bank Form Handling */
  const handleBankInputChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveBankDetails = async (e) => {
    e.preventDefault();
    if (!token) return alert("No auth token found");

    try {
      const res = await saveBankDetails(
        {
          account_holder_name: bankForm.account_holder_name,
          bank_name: bankForm.bank_name,
          bank_branch: bankForm.bank_branch,
          account_number: bankForm.account_number,
          expiry_month: bankForm.expiry_month
            ? Number(bankForm.expiry_month)
            : null,
          expiry_year: bankForm.expiry_year
            ? Number(bankForm.expiry_year)
            : null,
          cvc: bankForm.cvc || null,
        },
        token
      );

      alert(res.message || "Bank details saved");

      setWithdrawalSummary((prev) => ({ ...prev, bankDetailsExist: true }));
      setShowBankForm(false);
    } catch (err) {
      console.error("Error saving bank details:", err);
      alert("Failed to save bank details");
    }
  };

  /** Booking Card Component */
  const renderCard = (booking) => (
    <div key={booking.id} className="hbl-card">
      <h3 className="hbl-hotel-name">{booking.listing?.name}</h3>

      <div className="hbl-info-row">
        <p>
          <FaCalendarAlt className="hbl-icon" /> <strong>Booked:</strong>{" "}
          {new Date(booking.booking_date).toLocaleDateString()}
        </p>
        <p>
          <FaMapMarkerAlt className="hbl-icon" /> <strong>Traveler:</strong>{" "}
          {booking.user?.full_name}
        </p>
      </div>

      <div className="hbl-dates-row">
        <div className="hbl-date-box">
          <span className="hbl-label">Check-in</span>
          <span className="hbl-value">
            {new Date(booking.checking_date).toLocaleDateString()}
          </span>
        </div>

        <div className="hbl-date-box">
          <span className="hbl-label">Checkout</span>
          <span className="hbl-value">
            {new Date(booking.checkout_date).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="hbl-footer">
        <p>
          <strong>Guests:</strong> {booking.number_of_guests}
        </p>

        <div className="hbl-payment-wrap">
          <span
            className={`hbl-payment-status ${
              booking.payment_status_id === 1
                ? "paid"
                : booking.payment_status_id === 2
                ? "pending"
                : "failed"
            }`}
          >
            {booking.payment_status_id === 1
              ? "Paid"
              : booking.payment_status_id === 2
              ? "Pending"
              : "Failed"}
          </span>

          <p className="hbl-price">Rs. {booking.price}</p>
        </div>
      </div>
    </div>
  );

  /** Withdrawal Request */
  const handleWithdrawalRequest = async () => {
    const res = await requestAgentWithdrawal(token);
    alert(res.message);
  };

  return (
    <div className="hbl-container">
      <h2 className="hbl-title">Agent Hotel Bookings</h2>

      {/* Withdrawal Summary */}
      <div className="withdraw-card">
        <div className="withdraw-header">
          <h2>Withdrawal Summary</h2>
          <span className="withdraw-status-badge">
            {withdrawalSummary.status}
          </span>
        </div>

        <div className="withdraw-grid">
          <div className="withdraw-item">
            <h4>Bank Details</h4>
            <button
              className="hotel-summary-btn"
              onClick={() => setShowBankForm(true)}
            >
              {withdrawalSummary.bankDetailsExist
                ? "Edit Bank Details"
                : "Provide Bank Details"}
            </button>
          </div>

          <div className="withdraw-item">
            <h4>Eligible Amount</h4>
            <p className="amount">Rs. {withdrawalSummary.eligibleAmount}</p>
          </div>

          <div className="withdraw-item">
            <h4>Last Requested Amount</h4>
            <p className="amount">Rs. {withdrawalSummary.lastAmount}</p>
            <span className="date">
              {withdrawalSummary.requestedAt
                ? new Date(withdrawalSummary.requestedAt).toLocaleDateString()
                : "—"}
            </span>
          </div>

          <div className="withdraw-item">
            <h4>Withdrawal Status</h4>
            <p>{withdrawalSummary.status}</p>
          </div>
        </div>

        <button className="view-trans-btn" onClick={handleWithdrawalRequest}>
          Request Withdrawal
        </button>
      </div>

      {/* CURRENT MONTH WITHDRAWAL */}
      <div className="withdraw-card">
        <div className="withdraw-header">
          <h2>Current Month Withdrawal Amount</h2>
        </div>
        <div className="withdraw-grid single-item-grid">
          <div className="withdraw-item">
            <p className="amount" style={{ color: "green" }}>
              Rs. {currentMonthWithdrawal.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Withdrawal History */}
      <div className="withdraw-history-card">
        <h3 className="wh-title">Withdrawal History</h3>

        {withdrawalSummary.history.length === 0 ? (
          <p className="wh-empty">No previous withdrawals.</p>
        ) : (
          <div className="wh-list">
            {withdrawalSummary.history.map((item, index) => (
              <div className="wh-row" key={index}>
                <div className="wh-left">
                  <span className="wh-amount">Amount: {item.amount}</span>
                  <span className="wh-date">
                    Requested: {new Date(item.requestedAt).toLocaleString()}
                  </span>
                </div>

                <div
                  className={`wh-status ${
                    item.status === "Completed" ? "wh-completed" : "wh-pending"
                  }`}
                >
                  {item.status}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="hbl-tabs">
        {["all", "today", "upcoming", "past"].map((tab) => (
          <button
            key={tab}
            className={`hbl-tab-btn ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="hbl-search">
        <input
          type="text"
          placeholder="Search by hotel name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Slider */}
      <div className="hbl-slider-wrapper">
        <button className="hbl-arrow left" onClick={() => scroll("left")}>
          <FaChevronLeft />
        </button>

        <div className="hbl-slider" ref={sliderRef}>
          {filteredBookings.length > 0 ? (
            filteredBookings.map(renderCard)
          ) : (
            <p className="hbl-no-results">No bookings found.</p>
          )}
        </div>

        <button className="hbl-arrow right" onClick={() => scroll("right")}>
          <FaChevronRight />
        </button>
      </div>

      {/* Bank Details Modal */}
      {showBankForm && (
        <div className="modal-overlay" onClick={() => setShowBankForm(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-btn"
              onClick={() => setShowBankForm(false)}
            >
              &times;
            </button>

            <h2 className="modal-title">Add Bank Details</h2>
            <p className="modal-subtitle">
              Your withdrawals will be sent to this bank account
            </p>

            <form onSubmit={handleSaveBankDetails} className="modal-form">
              <div className="modal-input-group">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  name="account_holder_name"
                  value={bankForm.account_holder_name}
                  onChange={handleBankInputChange}
                  required
                />
              </div>

              <div className="modal-input-group">
                <label>Bank Name</label>
                <select
                  name="bank_name"
                  value={bankForm.bank_name}
                  onChange={handleBankInputChange}
                  required
                >
                  <option value="">Select Bank</option>
                  <option value="Bank of Ceylon (BOC)">
                    Bank of Ceylon (BOC)
                  </option>
                  <option value="People's Bank">People's Bank</option>
                  <option value="Commercial Bank">Commercial Bank</option>
                  <option value="Sampath Bank">Sampath Bank</option>
                  <option value="National Savings Bank (NSB)">
                    National Savings Bank (NSB)
                  </option>
                  <option value="Seylan Bank">Seylan Bank</option>
                  <option value="HNB (Hatton National Bank)">
                    HNB (Hatton National Bank)
                  </option>
                  <option value="Regional Development Bank (RDB)">
                    Regional Development Bank (RDB)
                  </option>
                </select>
              </div>

              <div className="modal-input-group">
                <label>Bank Branch</label>
                <input
                  type="text"
                  name="bank_branch"
                  value={bankForm.bank_branch}
                  onChange={handleBankInputChange}
                  required
                />
              </div>

              <div className="modal-input-group">
                <label>Account Number</label>
                <input
                  type="text"
                  name="account_number"
                  value={bankForm.account_number}
                  onChange={handleBankInputChange}
                  required
                />
              </div>

              {/* <div className="modal-row">
                <div className="modal-input-group">
                  <label>Expiry Month</label>
                  <input
                    type="number"
                    name="expiry_month"
                    value={bankForm.expiry_month}
                    onChange={handleBankInputChange}
                  />
                </div>

                <div className="modal-input-group">
                  <label>Expiry Year</label>
                  <input
                    type="number"
                    name="expiry_year"
                    value={bankForm.expiry_year}
                    onChange={handleBankInputChange}
                  />
                </div>

                <div className="modal-input-group">
                  <label>CVC</label>
                  <input
                    type="number"
                    name="cvc"
                    value={bankForm.cvc}
                    onChange={handleBankInputChange}
                  />
                </div>
              </div> */}

              <button className="modal-submit-btn" type="submit">
                Save Bank Details
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentHotelBookingList;
